import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import {
  Button,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  Grid,
  Paper,
  Stack,
  Divider,
  InputAdornment,
  FormHelperText,
  Chip,
  useTheme,
  alpha
} from '@mui/material';
import { getUserTravelPreferences, updateUserTravelPreferences } from 'src/features/user/user.slice';
import { useAppDispatch, useAppSelector } from 'src/store';
import { locationAndTravelSchema } from 'src/utils/formSchemas';
import ErrorPopup from '../ErrorPopup';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import { geocodeByAddress } from 'react-google-places-autocomplete';
import SuccessPopup from '../SuccessPopup';
import NumberFormat from 'react-number-format';
import { trackAnalytics } from 'src/features/analytics/analytics.slice';
import { compareChanges } from 'src/libs/utils';
import CustomMap, { calculateRadiusInMiles, calculateZoom } from '../BookingConfirmation/CustomMap';
import { delayTime } from '../ToastMessage';
import subscriptionPopup from '../Subscription/SubscriptionPopup';

const TravelForm = () => {
  const { subscriptionFeatures } = useAppSelector((state) => state.auth);
  const { travelPreferences, userError, userLoading } = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [value, setValue] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [geocodeError, setGeocodeError] = useState(null);

  const initialValues = {
    formatted_address: travelPreferences?.formatted_address || '',
    appointment_buffer: travelPreferences?.appointment_buffer || '',
    photographer_surcharge_travel_distance: travelPreferences?.photographer_surcharge_travel_distance || '',
    photographer_base_travel_distance: travelPreferences?.photographer_base_travel_distance || '',
    photographer_travel_surcharge: travelPreferences?.photographer_travel_surcharge || '',
    photographer_willing_to_surchage_travel: travelPreferences?.photographer_willing_to_surchage_travel || false,
    max_drive_time: travelPreferences?.max_drive_time || 120,
    snappiness_filter_max: travelPreferences?.snappiness_filter_max || '',
  };

  // Safe geocoding function
  const safeGeocodeByAddress = async (address) => {
    if (!address) return null;

    try {
      const results = await geocodeByAddress(address);
      if (results?.[0]) {
        return { value: results[0], label: results[0].formatted_address };
      }
      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  };

  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const response = await dispatch(getUserTravelPreferences()).unwrap();
        if (response?.data?.formatted_address) {
          const geocodeResult = await safeGeocodeByAddress(response.data.formatted_address);
          if (geocodeResult) {
            setValue(geocodeResult);
          }
        }
      } catch (error) {
        console.error('Error fetching preferences:', error);
      }
    };

    fetchPreferences();
  }, [dispatch]);

  const onSubmit = async (data) => {
    try {
      const payload = compareChanges(initialValues, data);
      const response = await dispatch(updateUserTravelPreferences(data)).unwrap();

      dispatch(trackAnalytics({
        action_name: "travel_preference_saved",
        payload: { ...payload, description: "user updated travel form" }
      }));

      setSuccessMessage("Successfully Updated");
      setTimeout(() => setSuccessMessage(""), delayTime);
    } catch (error) {
      console.error('Error updating preferences:', error);
    }
  };

  const googlePlacesStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: isDarkMode ? alpha(theme.palette.background.paper, 0.1) : theme.palette.background.paper,
      borderColor: isDarkMode ? alpha(theme.palette.divider, 0.1) : theme.palette.divider,
      borderRadius: theme.shape.borderRadius,
      padding: '8px 14px',
      minHeight: '56px',
      cursor: 'text',
      '&:hover': {
        borderColor: theme.palette.text.primary,
      }
    }),
    input: (provided) => ({
      ...provided,
      color: theme.palette.text.primary,
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: theme.palette.background.paper,
      borderRadius: theme.shape.borderRadius,
      boxShadow: theme.shadows[8],
      marginTop: 8,
    }),
    option: (provided, { isFocused, isSelected }) => ({
      ...provided,
      color: theme.palette.text.primary,
      backgroundColor: (isFocused || isSelected) 
        ? alpha(theme.palette.primary.main, 0.1) 
        : theme.palette.background.paper,
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
      }
    }),
    singleValue: (provided) => ({
      ...provided,
      color: theme.palette.text.primary,
    }),
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 600 }}>
        Advanced Scheduling Preferences
      </Typography>
      
      {userError && <ErrorPopup message={userError} />}
      {successMessage && <SuccessPopup message={successMessage} />}
      {geocodeError && <ErrorPopup message={geocodeError} />}

      <Formik
        initialValues={initialValues}
        validationSchema={locationAndTravelSchema}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({
          handleSubmit,
          handleChange,
          setFieldValue,
          resetForm,
          values,
          touched,
          errors,
        }) => (
          <Box component="form" noValidate onSubmit={handleSubmit}>
            {/* Buffer Section */}
            <Paper elevation={0} sx={{ p: 3, mb: 4, border: 1, borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                Buffer Settings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                The minimum amount of buffer time between appointments (before drive time buffers are added if applicable)
              </Typography>

              <TextField
                fullWidth
                label="Minimum Appointment Buffer"
                id="appointment_buffer"
                name="appointment_buffer"
                type="number"
                value={values.appointment_buffer}
                onChange={handleChange}
                disabled={userLoading}
                error={touched.appointment_buffer && !!errors.appointment_buffer}
                helperText={touched.appointment_buffer && errors.appointment_buffer}
                InputProps={{
                  endAdornment: <InputAdornment position="end">minutes</InputAdornment>,
                  inputProps: { min: 0 }
                }}
              />
            </Paper>

            {/* Filter Time Slots Section */}
            <Paper elevation={0} sx={{ p: 3, mb: 4, border: 1, borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                Filter Time Slots
              </Typography>
              
              <Stack spacing={3}>
                <Box>
                  <TextField
                    fullWidth
                    label="Maximum round trip drive time"
                    placeholder="Time in minutes"
                    id="max_drive_time"
                    name="max_drive_time"
                    type="number"
                    value={values.max_drive_time}
                    onChange={handleChange}
                    disabled={userLoading}
                    error={touched.max_drive_time && !!errors.max_drive_time}
                    helperText={
                      (touched.max_drive_time && errors.max_drive_time) || 
                      "Based on forecasted traffic at the proposed appointment time"
                    }
                    InputProps={{
                      endAdornment: <InputAdornment position="end">minutes</InputAdornment>,
                      inputProps: { min: 0 }
                    }}
                  />
                  <Chip label="Beta" size="small" color="primary" sx={{ mt: 1 }} />
                </Box>

                <TextField
                  fullWidth
                  label="Snappiness Threshold"
                  id="snappiness_filter_max"
                  name="snappiness_filter_max"
                  type="number"
                  value={values.snappiness_filter_max}
                  onChange={handleChange}
                  disabled={userLoading}
                  error={touched.snappiness_filter_max && !!errors.snappiness_filter_max}
                  helperText={
                    (touched.snappiness_filter_max && errors.snappiness_filter_max) ||
                    '"Snappiness" filters and shows the most efficient time slots based on your set percentage.'
                  }
                  InputProps={{
                    inputProps: { min: 0, max: 60 }
                  }}
                />
              </Stack>
            </Paper>

            {/* Distances Section */}
            <Paper elevation={0} sx={{ p: 3, mb: 4, border: 1, borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                Travel Distances
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Tell us how far you are willing to travel from your home address. We use historical
                traffic data to make your schedule more efficient.
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Maximum Distance"
                    placeholder="Distance"
                    id="photographer_surcharge_travel_distance"
                    name="photographer_surcharge_travel_distance"
                    type="number"
                    value={values.photographer_surcharge_travel_distance}
                    onChange={handleChange}
                    disabled={userLoading}
                    error={touched.photographer_surcharge_travel_distance && !!errors.photographer_surcharge_travel_distance}
                    helperText={
                      (touched.photographer_surcharge_travel_distance && errors.photographer_surcharge_travel_distance) ||
                      'Maximum distance willing to travel (Straight line distance "As the crow flies")'
                    }
                    InputProps={{
                      endAdornment: <InputAdornment position="end">miles</InputAdornment>,
                      inputProps: { min: 6 }
                    }}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                      Address <Typography component="span" color="error">*</Typography>
                    </Typography>
                    
                    <GooglePlacesAutocomplete
                      apiKey={process.env.REACT_APP_GOOGLE_MAP_API_KEY}
                      selectProps={{
                        value,
                        onChange: (e) => {
                          try {
                            setValue(e);
                            setFieldValue('formatted_address', e?.label || '');
                            setGeocodeError(null);
                          } catch (error) {
                            console.error('Error handling place selection:', error);
                            setGeocodeError('Error selecting location');
                          }
                        },
                        styles: googlePlacesStyles,
                      }}
                    />
                  </FormControl>

                  {values.formatted_address && (
                    <Box sx={{ mt: 3, height: '300px', position: "relative", borderRadius: 2, overflow: 'hidden' }}>
                      <ErrorBoundary>
                        <CustomMap
                          address={values.formatted_address}
                          radius={calculateRadiusInMiles(values.photographer_surcharge_travel_distance)}
                          zoom={calculateZoom(values.photographer_surcharge_travel_distance)}
                        />
                      </ErrorBoundary>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Paper>

            {/* Hidden Travel Fee Section */}
            <Box sx={{ display: 'none' }}>
              <Paper elevation={0} sx={{ p: 3, mb: 4, border: 1, borderColor: 'divider', borderRadius: 2 }}>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ mb: 2 }}>
                    Add A Fee For Drives Over A Certain Length? <span style={{ color: 'error.main' }}>*</span>
                  </FormLabel>
                  <RadioGroup
                    name="photographer_willing_to_surchage_travel"
                    value={values.photographer_willing_to_surchage_travel}
                    onChange={(e) => {
                      if (e.target.value === 'true' && subscriptionFeatures?.drive_time_travel_fees?.permitted) {
                        setFieldValue('photographer_willing_to_surchage_travel', true);
                      } else if (e.target.value === 'true') {
                        subscriptionPopup(subscriptionFeatures, "Travel fees not included", { 
                          feature_id: "drive_time_travel_fees", 
                          description: "Provider can not use travel fees" 
                        }, dispatch);
                      } else {
                        setFieldValue('photographer_willing_to_surchage_travel', false);
                      }
                    }}
                  >
                    <FormControlLabel 
                      value="true" 
                      control={<Radio />} 
                      label="Yes" 
                      disabled={userLoading}
                    />
                    <FormControlLabel 
                      value="false" 
                      control={<Radio />} 
                      label="No" 
                      disabled={userLoading}
                    />
                  </RadioGroup>
                  {errors.photographer_willing_to_surchage_travel && (
                    <FormHelperText error>
                      {errors.photographer_willing_to_surchage_travel}
                    </FormHelperText>
                  )}
                </FormControl>

                <Grid container spacing={3} sx={{ mt: 2 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Travel Time Included In Service Fees"
                      id="photographer_base_travel_distance"
                      name="photographer_base_travel_distance"
                      type="number"
                      value={values.photographer_base_travel_distance}
                      onChange={handleChange}
                      disabled={userLoading || !subscriptionFeatures?.drive_time_travel_fees?.permitted}
                      error={touched.photographer_base_travel_distance && !!errors.photographer_base_travel_distance}
                      helperText={touched.photographer_base_travel_distance && errors.photographer_base_travel_distance}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">minutes</InputAdornment>,
                        inputProps: { min: 0 }
                      }}
                      required
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Extra Charge Above Travel Time Included"
                      id="photographer_travel_surcharge"
                      name="photographer_travel_surcharge"
                      value={values.photographer_travel_surcharge}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        setFieldValue("photographer_travel_surcharge", value);
                      }}
                      disabled={userLoading || !subscriptionFeatures?.drive_time_travel_fees?.permitted}
                      error={touched.photographer_travel_surcharge && !!errors.photographer_travel_surcharge}
                      helperText={touched.photographer_travel_surcharge && errors.photographer_travel_surcharge}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        endAdornment: <InputAdornment position="end">USD</InputAdornment>,
                      }}
                      required
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Box>

            <Divider sx={{ my: 4 }} />
            
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button 
                variant="contained" 
                type="submit" 
                disabled={userLoading}
                size="large"
                sx={{ minWidth: 120 }}
              >
                Save
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => resetForm()}
                size="large"
                sx={{ minWidth: 120 }}
              >
                Cancel
              </Button>
            </Stack>
          </Box>
        )}
      </Formik>
    </Box>
  );
};

// Error boundary component to catch any rendering errors
interface ErrorBoundaryState {
  hasError: boolean;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('Map Error:', error, errorInfo);
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="error" gutterBottom>
            Unable to display map
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please try again later
          </Typography>
        </Paper>
      );
    }

    return this.props.children;
  }
}

export default TravelForm;
